import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

// Usar o mesmo domínio que a página está sendo servida (funciona em produção e preview)
const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';

const ViewerLimitsContext = createContext(null);

export const useViewerLimits = () => {
  const context = useContext(ViewerLimitsContext);
  if (!context) {
    // Retorna valores padrão se usado fora do provider
    return {
      isViewer: false,
      hasLimits: false,
      limitsStatus: null,
      loading: false,
      checkCreatorLimit: async () => ({ has_limits: false }),
      trackContentView: async () => ({ allowed: true, should_blur: false }),
      shouldBlockContent: () => false,
      isGlobalLimitReached: () => false,
      refreshLimits: () => {}
    };
  }
  return context;
};

export const ViewerLimitsProvider = ({ children }) => {
  const { user } = useAuth();
  const [limitsStatus, setLimitsStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatorLimitsCache, setCreatorLimitsCache] = useState({});
  const [trackedContent, setTrackedContent] = useState(new Set()); // Posts já trackados

  const isViewer = user?.role === 'viewer';
  const hasLimits = isViewer && limitsStatus?.has_limits;

  // Carregar status global dos limites
  const loadLimitsStatus = useCallback(async () => {
    if (!user || user.role !== 'viewer') {
      setLimitsStatus({ has_limits: false });
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/viewer/limits/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLimitsStatus(response.data);
    } catch (error) {
      console.error('Error loading viewer limits:', error);
      setLimitsStatus({ has_limits: false });
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Verificar limites para um criador específico
  const checkCreatorLimit = useCallback(async (creatorId) => {
    if (!user || user.role !== 'viewer' || !creatorId) {
      return { has_limits: false };
    }

    // Retornar cache se existir e for recente (5 min)
    const cached = creatorLimitsCache[creatorId];
    if (cached && Date.now() - cached.timestamp < 5 * 60 * 1000) {
      return cached.data;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/viewer/limits/check/${creatorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCreatorLimitsCache(prev => ({
        ...prev,
        [creatorId]: { data: response.data, timestamp: Date.now() }
      }));
      
      return response.data;
    } catch (error) {
      console.error('Error checking creator limit:', error);
      return { has_limits: false };
    }
  }, [user, creatorLimitsCache]);

  // Registrar visualização de conteúdo
  const trackContentView = useCallback(async (creatorId, contentType, postId = null) => {
    if (!user || user.role !== 'viewer') {
      return { allowed: true, should_blur: false };
    }

    // Evitar tracking duplicado do mesmo post
    const trackKey = `${creatorId}-${postId || 'unknown'}-${contentType}`;
    if (trackedContent.has(trackKey)) {
      return { allowed: true, should_blur: false };
    }

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        creator_id: creatorId,
        content_type: contentType
      });
      if (postId) params.append('post_id', postId);

      const response = await axios.post(
        `${API}/viewer/limits/track?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Marcar como tracked
      setTrackedContent(prev => new Set([...prev, trackKey]));

      // Invalidar cache do criador
      setCreatorLimitsCache(prev => {
        const updated = { ...prev };
        delete updated[creatorId];
        return updated;
      });

      // Atualizar status global se necessário
      if (!response.data.allowed) {
        await loadLimitsStatus();
      }

      return response.data;
    } catch (error) {
      console.error('Error tracking content view:', error);
      return { allowed: true, should_blur: false };
    }
  }, [user, trackedContent, loadLimitsStatus]);

  // Verificar se conteúdo de um criador está bloqueado
  const shouldBlockContent = useCallback((creatorId) => {
    if (!hasLimits) return false;

    const cached = creatorLimitsCache[creatorId];
    if (!cached) return false;

    const data = cached.data;
    return data.profile_blocked || data.global_blocked;
  }, [hasLimits, creatorLimitsCache]);

  // Verificar se limite global foi atingido
  const isGlobalLimitReached = useCallback(() => {
    if (!hasLimits || !limitsStatus?.usage?.global) return false;
    return limitsStatus.usage.global.limit_reached;
  }, [hasLimits, limitsStatus]);

  // Refresh dos limites
  const refreshLimits = useCallback(() => {
    setCreatorLimitsCache({});
    setTrackedContent(new Set());
    loadLimitsStatus();
  }, [loadLimitsStatus]);

  // Carregar limites ao montar ou quando usuário mudar
  useEffect(() => {
    loadLimitsStatus();
  }, [loadLimitsStatus]);

  // Limpar cache diariamente (meia-noite)
  useEffect(() => {
    const checkMidnight = () => {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 0, 0);
      const msUntilMidnight = midnight.getTime() - now.getTime();
      
      setTimeout(() => {
        refreshLimits();
        // Recomeçar o ciclo
        checkMidnight();
      }, msUntilMidnight);
    };

    checkMidnight();
  }, [refreshLimits]);

  const value = useMemo(() => ({
    isViewer,
    hasLimits,
    limitsStatus,
    loading,
    checkCreatorLimit,
    trackContentView,
    shouldBlockContent,
    isGlobalLimitReached,
    refreshLimits
  }), [
    isViewer,
    hasLimits,
    limitsStatus,
    loading,
    checkCreatorLimit,
    trackContentView,
    shouldBlockContent,
    isGlobalLimitReached,
    refreshLimits
  ]);

  return (
    <ViewerLimitsContext.Provider value={value}>
      {children}
    </ViewerLimitsContext.Provider>
  );
};

export default ViewerLimitsContext;
